import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { CameraIcon, CheckCircleIcon, ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Button } from '../components/ui/button';

// Página pública (móvil): subir material del día a una pieza del calendario vía QR
const SubirMaterial: React.FC = () => {
  const { token = '' } = useParams<{ token: string }>();
  const [info, setInfo] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadedCount, setUploadedCount] = useState(0);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!token) { setError('Link inválido'); setLoading(false); return; }
    axios.get(`/api/content-calendars/photo-upload/${token}/info`)
      .then((res) => setInfo(res.data))
      .catch((err) => setError(err.response?.data?.error || 'Link inválido o expirado'))
      .finally(() => setLoading(false));
  }, [token]);

  const handleFilesSelected = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const arr = Array.from(files);
    setSelectedFiles((prev) => [...prev, ...arr]);
    arr.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (e) => setPreviews((prev) => [...prev, e.target?.result as string]);
      reader.readAsDataURL(file);
    });
  };

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    setPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const uploadFiles = async () => {
    if (selectedFiles.length === 0) return;
    setUploading(true);
    let count = 0;
    try {
      for (const file of selectedFiles) {
        const formData = new FormData();
        formData.append('photo', file);
        await axios.post(`/api/content-calendars/photo-upload/${token}/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        count++;
      }
      setUploadedCount((c) => c + count);
      setSelectedFiles([]);
      setPreviews([]);
      toast.success(`${count} archivo${count > 1 ? 's' : ''} subido${count > 1 ? 's' : ''} correctamente`);
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Error al subir');
    } finally {
      setUploading(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-lg">Cargando...</div>;
  if (error || !info) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <ExclamationTriangleIcon className="h-16 w-16 mx-auto mb-4 text-red-500" />
          <h1 className="text-xl font-bold mb-2">Link no válido</h1>
          <p className="text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground p-4 flex flex-col">
      <div className="max-w-md mx-auto w-full space-y-4">
        <div className="bg-card border border-border rounded-xl p-4 shadow-sm">
          <div className="text-xs text-cyan-600 dark:text-cyan-400 font-medium">Material del día</div>
          <div className="text-2xl font-bold mt-1">{info.dayLabel}</div>
          <div className="text-sm text-muted-foreground">{info.calendarName} · {info.mes} {info.year}</div>
          {(uploadedCount > 0 || info.count > 0) && (
            <div className="flex items-center gap-1 mt-2 text-emerald-600 text-xs font-medium">
              <CheckCircleIcon className="h-4 w-4" />
              {uploadedCount > 0 ? `${uploadedCount} subido${uploadedCount > 1 ? 's' : ''} esta sesión` : `${info.count} archivo${info.count > 1 ? 's' : ''} en el día`}
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="w-full bg-cyan-600 hover:bg-cyan-700 text-white rounded-xl p-6 flex flex-col items-center gap-2 shadow-lg active:scale-95 transition-transform"
        >
          <CameraIcon className="h-12 w-12" />
          <span className="text-lg font-bold">Seleccionar fotos / videos</span>
          <span className="text-xs opacity-80">Cámara o galería</span>
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,video/*"
          multiple
          className="hidden"
          onChange={(e) => handleFilesSelected(e.target.files)}
        />

        {previews.length > 0 && (
          <div className="space-y-3">
            <p className="text-sm font-medium">Seleccionados ({previews.length})</p>
            <div className="grid grid-cols-3 gap-2">
              {previews.map((src, i) => (
                <div key={i} className="relative aspect-square">
                  <img src={src} alt={`Archivo ${i + 1}`} className="w-full h-full object-cover rounded-lg border border-border" />
                  <button type="button" onClick={() => removeFile(i)} className="absolute top-1 right-1 rounded-full bg-red-500 text-white p-0.5">
                    <XMarkIcon className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
            <Button onClick={uploadFiles} disabled={uploading} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white text-lg py-6">
              {uploading ? 'Subiendo...' : `Subir ${previews.length}`}
            </Button>
          </div>
        )}

        <p className="text-xs text-muted-foreground text-center">
          Seleccioná el material y tocá Subir. Va directo a la carpeta del día en el banco de medios.
        </p>
      </div>
    </div>
  );
};

export default SubirMaterial;
